import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getArticlesByCategory, articles as allArticles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const displayArticles = activeSlug === 'all' ? allArticles : getArticlesByCategory(activeSlug);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(newSlug === 'all' ? '/category/loli-culture' : `/category/${newSlug}`);
  };

  const pageTitle = currentCategory
    ? `${currentCategory.name} - Loli文化完全指南 | 萌之社 MoeLab`
    : '全部分类 - Loli文化完全指南 | 萌之社 MoeLab';

  const pageDesc = currentCategory
    ? `${currentCategory.description}。浏览${currentCategory.name}相关文章，深入了解二次元萌系文化。`
    : '浏览Loli文化与二次元萌系角色相关所有文章，涵盖loli文化、loli角色、萌系风格、ACG作品、插画风格等分类。';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: `https://mangast.github.io/category/${activeSlug}`,
    isPartOf: { '@id': 'https://mangast.github.io/#website' },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: displayArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://mangast.github.io/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  return (
    <SEO
      title={pageTitle}
      description={pageDesc}
      keywords={`${currentCategory?.name || '全部分类'},loli文化,二次元,萌系角色,ACG`}
      canonicalUrl={`https://mangast.github.io/category/${activeSlug}`}
    >
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: t('article.breadcrumb.home'), href: '/' },
                  ...(currentCategory
                    ? [{ label: currentCategory.name }]
                    : [{ label: '全部分类' }]),
                ]}
              />
            </div>

            <div className="mb-10">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                {currentCategory ? currentCategory.name : '全部分类'}
              </h1>
              <p className="mt-2 text-sm text-foreground-600">
                {currentCategory
                  ? currentCategory.description
                  : '浏览所有与loli文化、萌系角色相关的精彩文章'}
              </p>
            </div>

            <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {displayArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayArticles.map((article) => (
                  <a
                    key={article.id}
                    href={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
                  >
                    <div className="relative overflow-hidden h-48">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
                          {article.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-base font-bold text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed flex-1">
                        {article.excerpt}
                      </p>
                      <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime} 分钟阅读
                        </span>
                        <span>{article.date}</span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">{t('category.noArticles')}</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </SEO>
  );
}